import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';

const TVModeContext = createContext();

// Detect Smart TV / streaming device browsers
const detectTVDevice = () => {
  const ua = navigator.userAgent.toLowerCase();
  
  if (ua.includes('silk') && ua.includes('fire')) return { isTv: true, device: 'firetv', name: 'Fire TV' };
  if (ua.includes('tizen')) return { isTv: true, device: 'samsung', name: 'Samsung TV' };
  if (ua.includes('web0s') || ua.includes('webos')) return { isTv: true, device: 'lg', name: 'LG TV' };
  if (ua.includes('roku')) return { isTv: true, device: 'roku', name: 'Roku' };
  if (ua.includes('crkey') || ua.includes('chromecast')) return { isTv: true, device: 'chromecast', name: 'Chromecast' };
  if (ua.includes('appletv') || ua.includes('apple tv')) return { isTv: true, device: 'appletv', name: 'Apple TV' };
  if (ua.includes('smart-tv') || ua.includes('smarttv') || ua.includes('hbbtv')) return { isTv: true, device: 'smarttv', name: 'Smart TV' };
  if (ua.includes('playstation')) return { isTv: true, device: 'playstation', name: 'PlayStation' };
  if (ua.includes('xbox')) return { isTv: true, device: 'xbox', name: 'Xbox' };
  // Android TV
  if (ua.includes('android') && (ua.includes('tv') || ua.includes('aftm') || ua.includes('aftt') || ua.includes('aftb'))) {
    return { isTv: true, device: 'androidtv', name: 'Android TV' };
  }

  return { isTv: false, device: null, name: null };
};

const FOCUSABLE_SELECTOR = [
  '[data-tv-focusable]',
  'a[href]',
  'button:not(:disabled)',
  'input:not(:disabled)',
  'select:not(:disabled)',
  '[role="button"]',
  '[tabindex]:not([tabindex="-1"])',
  'video',
].join(', ');

export const TVModeProvider = ({ children }) => {
  const detectedDevice = detectTVDevice();
  const [tvMode, setTvMode] = useState(() => {
    const stored = localStorage.getItem('tvMode');
    if (stored !== null) return stored === 'true';
    return detectedDevice.isTv;
  });
  const [device] = useState(detectedDevice);
  const focusedRef = useRef(null);
  const focusRingRef = useRef(null);

  // Toggle TV mode
  const toggleTvMode = useCallback(() => {
    setTvMode(prev => {
      const next = !prev;
      localStorage.setItem('tvMode', String(next));
      return next;
    });
  }, []);

  // Get all focusable elements in view order
  // When a TV fullscreen video is active, scope focus to that container only
  const getFocusableElements = useCallback(() => {
    const fullscreenEl = document.querySelector('.tv-video-fullscreen');
    const root = fullscreenEl || document;
    const elements = Array.from(root.querySelectorAll(FOCUSABLE_SELECTOR));
    return elements.filter(el => {
      const style = window.getComputedStyle(el);
      const rect = el.getBoundingClientRect();
      return style.display !== 'none' && 
             style.visibility !== 'hidden' && 
             style.opacity !== '0' &&
             rect.width > 0 && rect.height > 0;
    });
  }, []);

  // Move focus ring to element
  const moveFocusTo = useCallback((el) => {
    if (!el || !focusRingRef.current) return;
    
    // Remove previous focus
    if (focusedRef.current) {
      focusedRef.current.classList.remove('tv-focused');
    }
    
    focusedRef.current = el;
    el.classList.add('tv-focused');
    
    // Position focus ring
    const rect = el.getBoundingClientRect();
    const ring = focusRingRef.current;
    ring.style.top = `${rect.top + window.scrollY - 4}px`;
    ring.style.left = `${rect.left + window.scrollX - 4}px`;
    ring.style.width = `${rect.width + 8}px`;
    ring.style.height = `${rect.height + 8}px`;
    ring.style.opacity = '1';
    
    // Scroll element into view
    el.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
  }, []);

  // Find nearest element in a direction
  const findNearest = useCallback((direction) => {
    const elements = getFocusableElements();
    if (!elements.length) return null;
    
    const current = focusedRef.current;
    if (!current) return elements[0];
    
    const currentRect = current.getBoundingClientRect();
    const cx = currentRect.left + currentRect.width / 2;
    const cy = currentRect.top + currentRect.height / 2;
    
    let best = null;
    let bestScore = Infinity;
    
    for (const el of elements) {
      if (el === current) continue;
      const rect = el.getBoundingClientRect();
      const ex = rect.left + rect.width / 2;
      const ey = rect.top + rect.height / 2;
      const dx = ex - cx;
      const dy = ey - cy;
      
      let isValidDirection = false;
      let score = 0;
      
      switch (direction) {
        case 'up':
          isValidDirection = dy < -10;
          score = Math.abs(dy) + Math.abs(dx) * 3;
          break;
        case 'down':
          isValidDirection = dy > 10;
          score = Math.abs(dy) + Math.abs(dx) * 3;
          break;
        case 'left':
          isValidDirection = dx < -10;
          score = Math.abs(dx) + Math.abs(dy) * 3;
          break;
        case 'right':
          isValidDirection = dx > 10;
          score = Math.abs(dx) + Math.abs(dy) * 3;
          break;
        default:
          break;
      }
      
      if (isValidDirection && score < bestScore) {
        bestScore = score;
        best = el;
      }
    }
    
    // Fallback: wrap around for up/down
    if (!best) {
      const idx = elements.indexOf(current);
      if (direction === 'down' || direction === 'right') {
        best = elements[(idx + 1) % elements.length];
      } else {
        best = elements[(idx - 1 + elements.length) % elements.length];
      }
    }
    
    return best;
  }, [getFocusableElements]);

  // Keyboard handler
  useEffect(() => {
    if (!tvMode) return;
    
    const handleKey = (e) => {
      let direction = null;
      
      switch (e.key) {
        case 'ArrowUp': direction = 'up'; break;
        case 'ArrowDown': direction = 'down'; break;
        case 'ArrowLeft': direction = 'left'; break;
        case 'ArrowRight': direction = 'right'; break;
        case 'Enter':
        case ' ':
          if (focusedRef.current) {
            e.preventDefault();
            focusedRef.current.click();
            // For inputs, focus them for typing
            if (focusedRef.current.tagName === 'INPUT' || focusedRef.current.tagName === 'SELECT') {
              focusedRef.current.focus();
            }
          }
          return;
        case 'Backspace':
          // Back button on remote
          window.history.back();
          return;
        default:
          return;
      }
      
      e.preventDefault();
      const next = findNearest(direction);
      if (next) moveFocusTo(next);
    };
    
    // Initialize focus on first element
    const initTimer = setTimeout(() => {
      const elements = getFocusableElements();
      if (elements.length && !focusedRef.current) {
        moveFocusTo(elements[0]);
      }
    }, 500);
    
    window.addEventListener('keydown', handleKey);
    return () => {
      window.removeEventListener('keydown', handleKey);
      clearTimeout(initTimer);
    };
  }, [tvMode, findNearest, moveFocusTo, getFocusableElements]);

  // Re-initialize focus after navigation
  useEffect(() => {
    if (!tvMode) return;
    
    const observer = new MutationObserver(() => {
      if (focusedRef.current && !document.body.contains(focusedRef.current)) {
        focusedRef.current = null;
        const elements = getFocusableElements();
        if (elements.length) moveFocusTo(elements[0]);
      }
    });
    
    observer.observe(document.body, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, [tvMode, getFocusableElements, moveFocusTo]);

  // Toggle body class
  useEffect(() => {
    document.body.classList.toggle('tv-mode', tvMode);
    if (!tvMode && focusRingRef.current) {
      focusRingRef.current.style.opacity = '0';
    }
    // Try fullscreen on TV
    if (tvMode && device.isTv) {
      try {
        document.documentElement.requestFullscreen?.();
      } catch {}
    }
  }, [tvMode, device.isTv]);

  return (
    <TVModeContext.Provider value={{ tvMode, toggleTvMode, device, moveFocusTo }}>
      {children}
      {/* Focus ring overlay */}
      <div
        ref={focusRingRef}
        className="tv-focus-ring"
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: 0,
          height: 0,
          pointerEvents: 'none',
          border: '3px solid #0ea5e9',
          borderRadius: '8px',
          boxShadow: '0 0 0 2px rgba(14, 165, 233, 0.3), 0 0 20px rgba(14, 165, 233, 0.4)',
          opacity: 0,
          transition: 'all 0.15s ease-out',
          zIndex: 9999,
        }}
      />
    </TVModeContext.Provider>
  );
};

export const useTVMode = () => {
  const ctx = useContext(TVModeContext);
  if (!ctx) return { tvMode: false, toggleTvMode: () => {}, device: { isTv: false } };
  return ctx;
};

export default TVModeContext;
